import { fetchGigyaJWTPromise } from 'api/gigya';
import {
  FAVORITE_ALL_PODCASTS_SLUG,
  FAVORITE_ALL_STATIONS_SLUG,
  FAVORITE_ALL_CLIPS_SLUG,
  CLIPS_IN_PROGRESS_SLUG,
} from 'globalConst/const';
import CONTENT_TYPES from 'globalConst/contentTypes-const';
import ClipCollection from 'api/models/ClipCollection';
import { queryRequest } from './client';
import { getIsLoggedIn } from 'store/reducers/user';
import { getStore } from 'store';
import { isEmpty, logger } from 'util/index';

import {
  getUser,
  getFavoritePodcasts,
  getFavoriteStations,
  getFavorites,
  setFavorite,
  createProgress,
  getPodcastProgress,
  getShowProgress,
  getClipsInProgressCollection,
} from './queries';
import { Podcast, PodcastCollection, Station, StationCollection, PodcastEpisode } from '../models';

/**
 * Fetch the User object from the Universal API
 * @param {string} userToken
 * @returns {Promise.<object>}
 */
export const fetchUser = (userToken) =>
  new Promise((resolve, reject) => {
    queryRequest({ token: userToken, query: getUser, usePost: true })
      .then((resp) => {
        const {
          data: { user },
        } = resp;

        if (!user) throw new Error('Malformed response. Expected a User object in the response.', resp);

        resolve(user);
      })
      .catch((err) => {
        logger.error(err);
        reject();
      });
  });

/**
 * Fetch a favorited podcasts from the universal api
 * @param {string} slug
 * @returns {Object} Podcasts
 */

export const fetchFavoritePodcasts = async (userToken, slug) => {
  const { data } =
    (await queryRequest({
      query: getFavoritePodcasts,
      variables: { slug },
      token: userToken,
      usePost: true,
    })) || {};
  const favoritePodcasts = new PodcastCollection(data.collection);
  return favoritePodcasts.items && favoritePodcasts.items.filter((item) => item.userPreference.favourite);
};

export const fetchClipsInProgress = async () => {
  const store = getStore();
  const state = store.getState();
  const isUserLoggedIn = getIsLoggedIn(state);
  if (!isUserLoggedIn) {
    return {
      contentTypeId: 'continueListeningClipCollection',
      showCollectionLoginModal: true,
      showEmptyState: true,
    };
  }
  try {
    const jwtToken = await fetchGigyaJWTPromise();

    const { data } = await queryRequest({
      query: getClipsInProgressCollection,
      variables: { slug: CLIPS_IN_PROGRESS_SLUG },
      token: jwtToken,
      usePost: true,
    });
    store.dispatch({ items: data.clips.getClips.clips, type: CONTENT_TYPES.CONTINUE_LISTENING_CLIP_COLLECTION });
    return new ClipCollection(data.clips);
  } catch (error) {
    return null;
  }
};

/**
 * Fetch a favorited stations from the universal api
 * @param {string} slug
 * @returns {Object} Stations
 */
export const fetchFavoriteStations = async (userToken, slug) => {
  const { data } =
    (await queryRequest({
      query: getFavoriteStations,
      variables: { slug },
      token: userToken,
      usePost: true,
    })) || {};
  const favoriteStations = new StationCollection(data.collection);
  return favoriteStations.items && favoriteStations.items.filter((item) => item.userPreference.favourite);
};

function filterFavorites(favorites) {
  const { items } = favorites || {};
  if (isEmpty(items)) return [];
  return items.filter((item) => item.userPreference.favourite);
}
export const fetchFavorites = async (userToken) => {
  try {
    const { data } = await queryRequest({
      query: getFavorites,
      variables: {
        stationsSlug: FAVORITE_ALL_STATIONS_SLUG,
        podcastsSlug: FAVORITE_ALL_PODCASTS_SLUG,
        clipsSlug: FAVORITE_ALL_CLIPS_SLUG,
      },
      token: userToken,
      usePost: true,
    });

    return {
      podcasts: filterFavorites(data.podcasts).map((item) => ({
        ...new Podcast(item),
      })),
      stations: filterFavorites(data.stations).map((item) => ({
        ...new Station(item),
      })),
      clips: filterFavorites(data.clips).map((item) => {
        const podcast = new Podcast(item.reference);

        return new PodcastEpisode({ ...item, podcastTitle: podcast.title, subTitle: item.title }, podcast);
      }),
    };
  } catch {
    return {
      podcasts: [],
      stations: [],
      clips: [],
    };
  }
};

/**
 * Fetch progress for a podcast or show from the universal api
 * @param {string} slug
 * @returns {Object} Progress Map
 */
export const fetchProgress = async ({ slug, contentTypeId }) => {
  const token = await fetchGigyaJWTPromise();

  let query;
  if (contentTypeId === CONTENT_TYPES.PODCAST) {
    query = getPodcastProgress;
  } else if (contentTypeId === CONTENT_TYPES.SHOW) {
    query = getShowProgress;
  } else if (contentTypeId === CONTENT_TYPES.CLIP_COLLECTION) {
    query = getClipsInProgressCollection;
  }

  if (!query) throw new Error(`fetchProgress not supported for ${contentTypeId}`);
  const request = {
    query,
    variables: { slug },
    token,
    usePost: true,
  };
  const { data } = (await queryRequest(request)) || {};

  if (contentTypeId === CONTENT_TYPES.CLIP_COLLECTION) {
    return data?.clips?.getClips?.clips.reduce((acc, { id, progress }) => ({ ...acc, [id]: progress }), {});
  }
  return (
    data && data.item.episodes && data.item.episodes.reduce((acc, { id, progress }) => ({ ...acc, [id]: progress }), {})
  );
};

/**
 * Mutating the userPreference object in the Universal API
 * @param {string} userToken
 * @param {string} contentId
 * @param {string} contentType
 * @param {boolean} favourite
 * @returns {Promise.<object>}
 */
export const mutateFavorite = (userToken, contentId, contentType, favourite) =>
  new Promise((resolve, reject) => {
    const userPreference = { contentId, contentType, favourite };
    queryRequest({
      query: setFavorite,
      variables: { userPreference },
      token: userToken,
      usePost: true,
    })
      .then((resp) => resolve(resp))
      .catch((err) => {
        logger.error(err);
        reject();
      });
  });

/**
 * Mutating the clipProgress object in the Universal API
 * @param {string} userToken
 * @param {string} contentId
 * @param {string} contentType
 * @param {string} eventType
 * @param {number} durationLength
 * @returns {Promise.<object>}
 */
export const mutateProgress = (userToken, contentId, eventType, durationLength) =>
  new Promise((resolve, reject) => {
    const inputProgress = {
      contentId,
      contentType: 'clip',
      eventType,
      durationLength,
    };

    queryRequest({
      query: createProgress,
      variables: { inputProgress },
      token: userToken,
      usePost: true,
    })
      .then((resp) => {
        resolve(resp);
      })
      .catch((err) => {
        logger.error(err);
        reject();
      });
  });
